import React, { useState, useEffect, useRef } from "react";
import {
  ChatMessage,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ToolCall,
} from "../../model_gen/chat";
import { formatCurrency, formatTextOrJson } from "../../utils/FormatUtils";
import ModelSelect from "../configuration/ModelSelect";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { cn } from "../../lib/utils";
import { 
  BotIcon, 
  UserIcon, 
  InfoIcon, 
  ClockIcon, 
  KeyIcon, 
  DollarSignIcon,
  TrashIcon,
  PlusIcon,
  CopyIcon
} from "lucide-react";
import { toast } from "sonner";

interface RequestOverviewProps {
  request: ChatCompletionRequest;
  response?: ChatCompletionResponse;
  isEditable?: boolean;
  onMessageEdit?: (index: number, content: string) => void;
  onMessageEditComplete?: (index: number, content: string) => void;
  onModelChange?: (model: string) => void;
  intentGroupId?: number;
  style?: React.CSSProperties;
  editingMessageIndex?: number | null;
  onEditingMessageChange?: (index: number | null) => void;
  editMode?: "all" | "final-assistant";
  showDetails?: boolean;
  expanded?: boolean;
  onToolCallChange?: (
    toolCallIndex: number,
    field: string,
    value: string
  ) => void;
  onAddToolCall?: () => void;
  onConfirmAddToolCall?: () => void;
  showAddToolCall?: boolean;
  newToolCall?: { name: string; arguments: string };
  setNewToolCall?: (toolCall: { name: string; arguments: string }) => void;
  setShowAddToolCall?: (show: boolean) => void;
  onDeleteToolCall?: (toolCallIndex: number) => void;
  annotationLabel?: any;
  editedToolCalls?: ToolCall[];
}

const RequestOverview: React.FC<RequestOverviewProps> = ({
  request,
  response,
  isEditable = false,
  onMessageEdit,
  onMessageEditComplete,
  onModelChange,
  intentGroupId,
  style,
  editingMessageIndex,
  onEditingMessageChange,
  editMode = "all",
  showDetails = true,
  expanded = false,
  onToolCallChange,
  onAddToolCall,
  onConfirmAddToolCall,
  showAddToolCall,
  newToolCall,
  setNewToolCall,
  setShowAddToolCall,
  onDeleteToolCall,
  editedToolCalls,
}) => {
  
  // Add ref for the container
  const containerRef = useRef<HTMLDivElement>(null);

  // Get messages array here first to avoid linter errors
  const messages = [
    ...(request?.params?.messages || []),
    ...(response?.choices?.[0]?.message ? [response.choices[0].message] : []),
  ];

  
  // Scroll to bottom when component mounts or messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [request, response, messages.length]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Request ID copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy request ID.");
    });
  };

  const renderRequestId = (requestId: string) => {
    // Show last 8 characters with ellipsis prefix for better readability
    const truncatedId = `...${requestId.slice(-8)}`;
    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
          {truncatedId}
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(requestId);
          }}
        >
          <CopyIcon className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  const getMessageIcon = (role: string) => {
    switch (role) {
      case "user":
        return <UserIcon className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case "assistant":
        return <BotIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case "system":
        return <InfoIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />;
      default:
        return null;
    }
  };

  const getMessageStyle = (role: string) => {
    switch (role) {
      case "user":
        return "bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800";
      case "assistant":
        return "bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800";
      case "system":
        return "bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800";
      default:
        return "bg-muted/50 border-border";
    }
  };

  // Get all message indices for defaultValue when expanded
  const defaultActiveItems = expanded
    ? messages.map((_, index) => index.toString())
    : [messages.length - 1].map(String);

  // Function to determine if a message is editable
  const isMessageEditable = (index: number, message: ChatMessage) => {
    if (!isEditable) return false;

    if (editMode === "final-assistant") {
      return index === messages.length - 1 && message.role === "assistant";
    }

    // In "all" mode, allow editing any message if isEditable is true
    return true;
  };

  const isMessageEdited = (index: number) => {
    // Check for server-side edits
    const isLastMessage = index === (request.params.messages?.length || 0) - 1;
    return (
      isLastMessage &&
      (request.metadata?._maitai_annotation_label !== undefined ||
        request.metadata?._maitai_test_request_response !== undefined ||
        (request.metadata?._maitai_test_request_response_tool_calls &&
          (request.metadata?._maitai_test_request_response_tool_calls as any[])
            .length > 0))
    );
  };

  const getMessageContent = (message: ChatMessage, index: number) => {
    // Check for metadata overrides in the last message
    const isLastMessage = index === messages.length - 1;
    if (isLastMessage) {
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.content;
      }
      const testResponse = request.metadata?._maitai_test_request_response as
        | string
        | undefined;
      if (testResponse !== undefined && testResponse !== null) {
        return testResponse;
      }
    }
    return message.content;
  };

  const getMessageToolCalls = (message: ChatMessage, index: number) => {
    const isLastMessage = index === messages.length - 1;
    
    if (isLastMessage && message.role === "assistant") {
      // First check if this is the response message (last message from response.choices[0].message)
      if (response?.choices?.[0]?.message?.tool_calls) {
        return response.choices[0].message.tool_calls;
      }
      
      // Check for test response tool calls
      if (request.metadata?._maitai_test_request_response_tool_calls) {
        return request.metadata._maitai_test_request_response_tool_calls as ToolCall[];
      }
      
      // Check for annotation label tool calls
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.tool_calls;
      }
    }
    
    return message.tool_calls;
  };

  const formatToolCallArguments = (args: string) => {
    try {
      // Parse the JSON string and format it nicely
      const parsedArgs = JSON.parse(args);
      return JSON.stringify(parsedArgs, null, 2);
    } catch (e) {
      // If it's not valid JSON, return as-is
      return args;
    }
  };


  const renderModelSelect = (value: string) => {
    return (
      <ModelSelect
        value={value}
        onChange={onModelChange}
        placeholder="Select a model"
        label=""
        showEmptyOption={false}
        intentGroupId={intentGroupId}
        modelType="all"
      />
    );
  };

  const renderToolCalls = (message: ChatMessage, index: number) => {
    // Use edited tool calls if available, otherwise get tool calls from message or metadata
    const messageToolCalls = getMessageToolCalls(message, index);
    const toolCallsToRender = (editedToolCalls && editedToolCalls.length > 0) ? editedToolCalls : (messageToolCalls || []);
    
    if (!isMessageEditable(index, message)) {
      if (!toolCallsToRender.length) return null;
      return (
        <div className="mt-2 space-y-2">
          <p className="text-sm font-medium">Tool Calls:</p>
          {toolCallsToRender.map((toolCall: ToolCall, i: number) => (
            <div
              key={toolCall.id || i}
              className="border-b border-border pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
            >
              <div className="mb-1">
                <span className="text-sm font-medium text-foreground">Function:</span>{" "}
                <span className="text-sm text-foreground">{toolCall.function.name}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-foreground">Arguments:</span>
                <pre className="mt-1 p-2 bg-muted rounded text-sm overflow-x-auto text-foreground">
                  {formatToolCallArguments(toolCall.function.arguments)}
                </pre>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-2 space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-sm font-medium text-foreground">Tool Calls</p>
          <Button size="sm" onClick={onAddToolCall || (() => {})}>
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Tool Call
          </Button>
        </div>

        {toolCallsToRender.map((toolCall: ToolCall, toolIndex: number) => (
          <div
            key={toolCall.id || toolIndex}
            className="border-b border-border pb-2 mb-2 last:border-b-0 last:pb-0 last:mb-0"
          >
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-foreground">Function:</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteToolCall?.(toolIndex)}
                className="text-destructive hover:text-destructive"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
            <Input
              value={toolCall.function.name}
              onChange={(e) =>
                onToolCallChange?.(toolIndex, "name", e.target.value)
              }
              className="mb-2"
              placeholder="Function name"
            />
            <div className="mb-1">
              <span className="text-sm font-medium text-foreground">Arguments:</span>
            </div>
            <Textarea
              value={toolCall.function.arguments}
              onChange={(e) =>
                onToolCallChange?.(toolIndex, "arguments", e.target.value)
              }
              className="min-h-[80px] max-h-[200px]"
              placeholder="Function arguments"
            />
          </div>
        ))}

        {showAddToolCall && (
          <div className="border border-blue-200 rounded p-2 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-800">
            <div className="mb-2">
              <span className="text-sm font-medium text-foreground">Function:</span>
              <Input
                value={newToolCall?.name || ""}
                onChange={(e) =>
                  setNewToolCall?.({ ...newToolCall!, name: e.target.value })
                }
                className="mt-1"
                placeholder="Function name"
              />
            </div>
            <div className="mb-2">
              <span className="text-sm font-medium text-foreground">Arguments:</span>
              <Textarea
                value={newToolCall?.arguments || ""}
                onChange={(e) =>
                  setNewToolCall?.({
                    ...newToolCall!,
                    arguments: e.target.value,
                  })
                }
                className="mt-1 min-h-[60px] max-h-[120px]"
                placeholder="Function arguments"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddToolCall?.(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onConfirmAddToolCall || (() => {})}
                disabled={!newToolCall?.name.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="border-border rounded-lg mb-6" style={style}>
      {showDetails && (
        <CardContent className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Tooltip content="Model used for this request">
              <Card className="text-center border-border">
                <CardContent className="p-6">
                  <BotIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Model</p>
                  {isEditable ? (
                    renderModelSelect(request.params.model)
                  ) : (
                    <p className="font-gc-med text-foreground">{request.params.model}</p>
                  )}
                </CardContent>
              </Card>
            </Tooltip>

            <Tooltip content="Time taken to process the request">
              <Card className="text-center border-border">
                <CardContent className="p-6">
                  <ClockIcon className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Time</p>
                  <p className="font-gc-med text-foreground">
                    {response?.response_time !== undefined && response.response_time !== null
                      ? `${response.response_time}ms`
                      : "N/A"}
                  </p>
                </CardContent>
              </Card>
            </Tooltip>

            <Tooltip content="Number of tokens used">
              <Card className="text-center border-border">
                <CardContent className="p-6">
                  <KeyIcon className="w-6 h-6 text-orange-600 dark:text-orange-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Tokens</p>
                  <p className="font-gc-med text-foreground">
                    {`${response?.usage?.prompt_tokens || 0} > ${
                      response?.usage?.completion_tokens || 0
                    }`}
                  </p>
                </CardContent>
              </Card>
            </Tooltip>

            <Tooltip content="Cost of the request">
              <Card className="text-center border-border">
                <CardContent className="p-6">
                  <DollarSignIcon className="w-6 h-6 text-pink-600 dark:text-pink-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2 font-gc-reg">Cost</p>
                  <p className="font-gc-med text-foreground">
                    {response?.usage?.cost && response.usage.cost > 0
                      ? formatCurrency(response.usage.cost)
                      : "N/A"}
                  </p>
                </CardContent>
              </Card>
            </Tooltip>
          </div>
        </CardContent>
      )}

      <div ref={containerRef}>
        <CardContent className={cn("p-8", showDetails && "pt-0")}>
          <Accordion
            type="multiple"
            defaultValue={defaultActiveItems}
            className="w-full"
          >
            {messages.map((message: ChatMessage, index) => (
              <AccordionItem
                key={index}
                value={index.toString()}
                className={cn(
                  "mb-4 rounded-lg border last:mb-0",
                  getMessageStyle(message.role)
                )}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    {getMessageIcon(message.role)}
                    <span className="font-gc-med capitalize">
                      {message.role}
                      {isMessageEdited(index) && "*"}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div
                    className={cn(
                      "whitespace-pre-wrap p-4 rounded bg-background/50 text-sm font-gc-reg text-foreground",
                      isMessageEditable(index, message) && "cursor-pointer hover:bg-background/80"
                    )}
                    onClick={() =>
                      isMessageEditable(index, message) &&
                      onEditingMessageChange?.(index)
                    }
                  >
                    {formatTextOrJson(getMessageContent(message, index))}
                  </div>
                  {renderToolCalls(message, index)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </div>
    </Card>
  );
};

export default RequestOverview;
